import React, { useMemo, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { boardGameProducts, formatZAR } from '../Components/ProductRetrieval'
import Pagination from '../Components/Pagination/Pagination'
import { searchProducts } from '../Utils/searchProducts'
import { useCart } from '../Components/Cart'
import { useFilterParams, FilterBar } from '../Components/FilterBar'

const ITEMS_PER_PAGE = 20

const BoardGames = () => {
  const { setParam, setCurrentPage, read } = useFilterParams()
  const [justAddedId, setJustAddedId] = useState(null)
  const { addToCart } = useCart()

  const currentPage = parseInt(read('page', '1'))
  const sortBy = read('sort', 'name-asc')
  const searchTerm = read('q')

  const handleAddToCart = useCallback((product) => {
    addToCart(product.id, product.name)
    setJustAddedId(product.id)
    setTimeout(() => setJustAddedId(null), 2000)
  }, [addToCart])

  const filteredProducts = useMemo(() => {
    const searched = searchTerm ? searchProducts(boardGameProducts, searchTerm) : boardGameProducts
    const sorted = [...searched]
    switch (sortBy) {
      case 'name-asc':
        sorted.sort((a, b) => a.name.localeCompare(b.name))
        break
      case 'name-desc':
        sorted.sort((a, b) => b.name.localeCompare(a.name))
        break
      case 'price-asc':
        sorted.sort((a, b) => a.price - b.price)
        break
      case 'price-desc':
        sorted.sort((a, b) => b.price - a.price)
        break
      default:
        break
    }
    return sorted
  }, [sortBy, searchTerm])

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE)
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  return (
    <div className="min-h-screen bg-[#fde1ff] text-gray-900">
      <section className="border-b border-gray-200 bg-gradient-to-br from-emerald-700 via-teal-700 to-emerald-900 px-4 py-14 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-emerald-200">
            Tagers Shoppe
          </p>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">
            Board Games
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-emerald-100">
            Discover a curated selection of strategy, family, and hobby board
            games — priced in South African Rand and ready to ship nationwide.
          </p>
          <p className="mt-3 text-sm text-emerald-200">
            All prices include VAT.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <FilterBar
          sortBy={sortBy}
          onSortChange={(v) => setParam('sort', v)}
          searchTerm={searchTerm}
          onSearchChange={(v) => setParam('q', v)}
          searchPlaceholder="Search board games…"
          onClear={() => {}}
          activeFilterCount={0}
          resultCount={paginatedProducts.length}
          totalCount={filteredProducts.length}
          colorScheme="emerald"
        />

        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          colorScheme="emerald"
        /> <br/>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {paginatedProducts.map((product) => {
            const isOutOfStock = product.inStock === 0
            const isPreorder = product.inStock === 2

            return (
              <article
                key={product.id}
                className="group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-md"
              >
                <Link to={`/Product/${product.id}`} className="flex flex-1 flex-col">
                  <div className="relative aspect-square bg-gradient-to-br from-gray-100 to-gray-200">
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        className={`h-full w-full object-contain max-h-[280px] ${isOutOfStock ? 'grayscale' : ''}`}
                      />
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                        <span className="text-sm font-medium text-gray-500">
                          No image available
                        </span>
                      </div>
                    )}
                    {isOutOfStock && (
                      <span className="absolute left-3 top-3 rounded-full bg-gray-600 px-2.5 py-1 text-xs font-semibold text-white">
                        Out of stock
                      </span>
                    )}
                    {isPreorder && (
                      <span className="absolute left-3 top-3 rounded-full bg-amber-500 px-2.5 py-1 text-xs font-semibold text-white">
                        Pre-order
                      </span>
                    )}
                  </div>

                  <div className="flex flex-1 flex-col p-4 text-left">
                    <h2 className={`line-clamp-2 text-base font-semibold ${
                      isOutOfStock ? 'text-gray-500' : 'text-gray-900 group-hover:text-emerald-700'
                    }`}>
                      {product.name}
                    </h2>
                    <p className={`mt-auto pt-4 text-lg font-bold ${isOutOfStock ? 'text-gray-400' : 'text-gray-900'}`}>
                      {formatZAR(product.price)}
                    </p>
                  </div>
                </Link>
                <div className="px-4 pb-4">
                  <button
                    onClick={() => handleAddToCart(product)}
                    disabled={isOutOfStock}
                    className={`w-full rounded-lg px-4 py-2 text-sm font-semibold text-white transition ${
                      isOutOfStock
                        ? 'bg-gray-400 cursor-not-allowed'
                        : justAddedId === product.id
                        ? 'bg-emerald-600'
                        : 'bg-emerald-700 hover:bg-emerald-800'
                    }`}
                  >
                    {justAddedId === product.id
                      ? 'Added to cart'
                      : isOutOfStock
                      ? 'Out of stock'
                      : 'Add to cart'}
                  </button>
                </div>
              </article>
            )
          })}
        </div>

        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          colorScheme="emerald"
        />
      </section>
    </div>
  )
}

export default BoardGames
